import { Suspense } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import { Button } from "@/components/ui/button";
import { assetUrl } from "@/lib/api";
import { ROLE_LABELS } from "@/lib/permissions";
import {
  ChartLineUp,
  Kanban,
  MicrophoneStage,
  UsersThree,
  SignOut,
  List,
  X,
  CalendarBlank,
  VideoCamera,
  ListChecks,
  AddressBook,
  ChartBar,
  Buildings,
  Question,
  Sun,
  Moon,
  PaintBrush,
  Gear,
  CalendarStar,
} from "@phosphor-icons/react";
import { useState } from "react";
import NotificationBell from "@/components/NotificationBell";

const NAV = [
  { to: "/", label: "Dashboard", icon: ChartLineUp, testid: "nav-dashboard", end: true, module: "dashboard" },
  { to: "/pipeline", label: "Sales Tracker", icon: Kanban, testid: "nav-pipeline", module: "sales" },
  { to: "/projects", label: "Projects", icon: MicrophoneStage, testid: "nav-projects", module: "projects" },
  { to: "/all-tasks", label: "All Tasks", icon: ListChecks, testid: "nav-all-tasks", module: "projects" },
  { to: "/calendar", label: "Calendar", icon: CalendarBlank, testid: "nav-calendar", module: "calendar" },
  { to: "/shoots", label: "Shoot Schedule", icon: VideoCamera, testid: "nav-shoots", module: "shoot_schedule" },
  { to: "/planner", label: "Planner", icon: CalendarStar, testid: "nav-planner", module: "projects" },
  { to: "/design", label: "Design Requests", icon: PaintBrush, testid: "nav-design", module: "projects" },
  { to: "/settings", label: "Settings", icon: Gear, testid: "nav-settings", module: "projects" },
  { to: "/team", label: "Team", icon: UsersThree, testid: "nav-team", module: "team" },
  { to: "/guests", label: "Guest Directory", icon: AddressBook, testid: "nav-guests", module: "shoot_schedule" },
  { to: "/shows", label: "Show Performance", icon: ChartBar, testid: "nav-shows", module: "show_performance" },
  { to: "/catalogue", label: "Catalogue", icon: Buildings, testid: "nav-catalogue", module: "show_performance" },
  { to: "/help", label: "Help & Training", icon: Question, testid: "nav-help" },
];

export default function Layout() {
  const { user, logout, can } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const doLogout = async () => {
    await logout();
    navigate("/login");
  };

  const visibleNav = NAV.filter((item) => !item.module || can(item.module, "view"));

  const Sidebar = () => (
    <aside className="w-64 shrink-0 border-r border-line bg-canvas flex flex-col p-5 gap-6 min-h-screen">
      <div className="flex items-center gap-3">
        <div className="w-11 h-11 rounded-md bg-[#FF3B30] flex items-center justify-center flex-shrink-0">
          <MicrophoneStage size={24} weight="fill" className="text-white" />
        </div>
        <div>
          <div className="font-heading font-black text-xl leading-none">PodOS</div>
          <div className="ui-label mt-1">Production&nbsp;Hub</div>
        </div>
      </div>

      <nav className="flex flex-col gap-1">
        {visibleNav.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.end}
            data-testid={item.testid}
            onClick={() => setOpen(false)}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm transition-colors ${
                isActive
                  ? "bg-panel text-fg border-l-2 border-[#FF3B30]"
                  : "text-fg2 hover:bg-surface hover:text-fg"
              }`
            }
          >
            <item.icon size={18} weight="duotone" />
            {item.label}
          </NavLink>
        ))}
      </nav>

      <div className="mt-auto flex flex-col gap-3">
        <button
          onClick={() => { setOpen(false); navigate("/profile"); }}
          data-testid="profile-link"
          className="p-3 rounded-md border border-line bg-surface text-left hover:border-[#FF3B30]/40 transition-colors flex items-center gap-3"
        >
          {user?.avatar_url ? (
            <img src={assetUrl(user.avatar_url)} alt="" className="w-9 h-9 rounded-full object-cover flex-shrink-0" />
          ) : (
            <div className="w-9 h-9 rounded-full bg-line flex items-center justify-center font-heading text-sm flex-shrink-0">
              {user?.name?.[0] || "?"}
            </div>
          )}
          <div className="min-w-0 flex-1">
            <div className="text-sm truncate" data-testid="current-user-name">{user?.name}</div>
            <div className="text-[10px] font-mono uppercase tracking-wider text-[#FF3B30] truncate">
              {ROLE_LABELS[user?.role] || user?.role}
            </div>
          </div>
        </button>
        <Button
          variant="ghost"
          className="justify-start text-fg2 hover:text-fg"
          onClick={toggleTheme}
          data-testid="theme-toggle"
        >
          {theme === "dark" ? <Sun size={16} className="mr-2" /> : <Moon size={16} className="mr-2" />}
          {theme === "dark" ? "Light mode" : "Dark mode"}
        </Button>
        <Button
          variant="ghost"
          className="justify-start text-fg2 hover:text-fg"
          onClick={doLogout}
          data-testid="logout-button"
        >
          <SignOut size={16} className="mr-2" /> Sign out
        </Button>
        <div className="text-[10px] font-mono uppercase tracking-widest text-[#52525B] text-center pt-1" data-testid="app-version">
          Production Hub · v1.6
        </div>
      </div>
    </aside>
  );

  return (
    <div className="min-h-screen flex">
      {/* Desktop */}
      <div className="hidden md:block">
        <Sidebar />
      </div>

      {/* Mobile drawer */}
      {open && (
        <div className="md:hidden fixed inset-0 z-50 flex">
          <Sidebar />
          <button
            aria-label="close"
            className="flex-1 bg-black/60"
            onClick={() => setOpen(false)}
          />
        </div>
      )}

      <main className="flex-1 min-w-0">
        <div className="md:hidden flex items-center justify-between px-4 h-14 border-b border-line sticky top-0 bg-canvas z-40">
          <div className="flex items-center gap-2">
            <img src="https://customer-assets.emergentagent.com/job_podcast-stages/artifacts/k4379v3m_DPN%20White.png" alt="PodOS" className="w-7 h-7 rounded" />
            <span className="font-heading font-bold">PodOS</span>
          </div>
          <div className="flex items-center gap-1">
            <NotificationBell />
            <button
              data-testid="mobile-menu-toggle"
              onClick={() => setOpen(true)}
              className="p-2 rounded-md hover:bg-surface"
            >
              {open ? <X size={20} /> : <List size={20} />}
            </button>
          </div>
        </div>
        <div className="hidden md:flex items-center justify-end px-8 h-12 border-b border-line">
          <NotificationBell />
        </div>
        <Suspense
          fallback={
            <div className="flex items-center justify-center py-32" data-testid="page-loading">
              <div className="h-6 w-6 rounded-full border-2 border-line border-t-[#FF3B30] animate-spin" />
            </div>
          }
        >
          <Outlet />
        </Suspense>
      </main>
    </div>
  );
}
